import React from "react";
import { TextField } from "@mui/material";
import type { UseFormRegister, FieldValues } from "react-hook-form";
import { axiosInstance } from "@/providers/data";

/** Form key of the handle; the generic `PATCH /user/:id` and `POST /user` take it. */
export const HANDLE_FIELD = "handle";

// How long typing has to pause before the check goes to the server, as the
// list's search waits.
const CHECK_DEBOUNCE_MS = 400;

type Reason = "empty" | "reserved" | "taken";

/** What `GET /user/:id/handle-available` answers (immersive-server `src/lib/handles.ts`). */
interface Availability {
  handle: string;
  available: boolean;
  reason: Reason | null;
}

export type HandleCheck =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "answered"; answer: Availability }
  | { state: "failed" };

const REFUSAL: Record<Reason, (handle: string) => string> = {
  empty: () => "Use letters, digits or underscores",
  reserved: (handle) => `@${handle} is reserved`,
  taken: (handle) => `@${handle} is taken`,
};

/** True while the handle typed cannot be saved: the server would answer 400 or 409. */
export const handleBlocked = (check: HandleCheck): boolean =>
  check.state === "answered" && !check.answer.available;

/**
 * Asks the server whether `value` can be user `userId`'s handle, once typing
 * pauses. `userId` 0 asks for a new account: no user has id 0, so a handle is
 * free exactly when nobody holds it (the create page has no id yet).
 *
 * Unchanged from `onFile`, it asks nothing: the account's own handle is free to
 * it. A failed request reads as `failed`, which does not block Save: the
 * server checks again on the write and its 409 is shown then.
 */
export function useHandleCheck(userId: number | string | undefined, value: string, onFile: string): HandleCheck {
  const [check, setCheck] = React.useState<HandleCheck>({ state: "idle" });

  React.useEffect(() => {
    const typed = value.trim();
    if (userId === undefined || typed === onFile) {
      setCheck({ state: "idle" });
      return;
    }
    setCheck({ state: "checking" });
    // A reply for an older value is dropped: only the latest effect's flag stays true.
    let current = true;
    const handle = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(`/api/v2/user/${userId}/handle-available`, {
          params: { handle: typed },
        });
        if (current) setCheck({ state: "answered", answer: data.data as Availability });
      } catch {
        if (current) setCheck({ state: "failed" });
      }
    }, CHECK_DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(handle);
    };
  }, [userId, value, onFile]);

  return check;
}

/**
 * The public @handle, with the server's answer under it while it is edited.
 * Stored cleaned (lower case, letters, digits and underscores, at most 24): the
 * line under the field shows the cleaned form, which is what Save stores.
 */
export function HandleField({
  register,
  check,
  emptyHelp,
}: {
  register: UseFormRegister<FieldValues>;
  check: HandleCheck;
  /** The line shown while nothing is being checked. */
  emptyHelp: string;
}) {
  const blocked = handleBlocked(check);
  let help = emptyHelp;
  if (check.state === "checking") help = "Checking…";
  else if (check.state === "failed") help = "Could not check this handle; Save checks it again";
  else if (check.state === "answered")
    help = check.answer.available
      ? `@${check.answer.handle} is available`
      : REFUSAL[check.answer.reason ?? "taken"](check.answer.handle);

  return (
    <TextField
      {...register(HANDLE_FIELD)}
      error={blocked}
      helperText={help}
      FormHelperTextProps={{ sx: check.state === "answered" && !blocked ? { color: "success.main" } : undefined }}
      margin="normal"
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={{ startAdornment: <span style={{ opacity: 0.6, marginRight: 2 }}>@</span> }}
      type="text"
      label="Handle"
      name={HANDLE_FIELD}
    />
  );
}
